export class LmsResumeStorageOverride {
	private lsGetItemOrig: Storage['getItem'] | null = null;
	private lsSetItemOrig: Storage['setItem'] | null = null;
	private lsRemoveItemOrig: Storage['removeItem'] | null = null;

	constructor(
		private readonly opts: {
			isResumeLikeValue: (value: string) => boolean;
			debug?: (...args: any[]) => void;
		}
	) {}

	enable(): void {
		if (this.lsGetItemOrig != null) {
			return;
		}
		try {
			if (typeof localStorage === 'undefined') {
				return;
			}

			this.lsGetItemOrig = localStorage.getItem.bind(localStorage);
			this.lsSetItemOrig = localStorage.setItem.bind(localStorage);
			this.lsRemoveItemOrig = localStorage.removeItem.bind(localStorage);

			localStorage.getItem = (key: string) => {
				const v = this.lsGetItemOrig!.call(localStorage, key);
				if (v != null && this.opts.isResumeLikeValue(v)) {
					return null;
				}
				return v;
			};

			localStorage.setItem = (key: string, value: string) => {
				if (this.opts.isResumeLikeValue(value)) {
					return;
				}
				this.lsSetItemOrig!.call(localStorage, key, value);
			};

			this.opts.debug?.('LMS resume: parent localStorage override active (same-origin iframe)');
		} catch (err) {
			this.opts.debug?.('LmsResumeStorageOverride.enable failed', err);
		}
	}

	/** Clear Storyline resume data before each challenge iframe load (does not block storage during navigation). */
	prepareForFreshChallengeLoad(): void {
		this.enable();
		this.purgeResumeLikeEntries(this.lsGetItemOrig, this.lsRemoveItemOrig, localStorage);
		this.purgeResumeLikeEntries(
			typeof sessionStorage !== 'undefined' ? sessionStorage.getItem.bind(sessionStorage) : null,
			typeof sessionStorage !== 'undefined' ? sessionStorage.removeItem.bind(sessionStorage) : null,
			typeof sessionStorage !== 'undefined' ? sessionStorage : null
		);
		this.disable();
	}

	private purgeResumeLikeEntries(
		getItem: Storage['getItem'] | null,
		removeItem: Storage['removeItem'] | null,
		storage: Storage | null
	): void {
		try {
			if (getItem == null || removeItem == null || storage == null) {
				return;
			}

			const keys: string[] = [];
			for (let i = 0; i < storage.length; i++) {
				const k = storage.key(i);
				if (k) {
					keys.push(k);
				}
			}

			let removed = 0;
			for (const k of keys) {
				const v = getItem.call(storage, k);
				if (v != null && this.opts.isResumeLikeValue(v)) {
					removeItem.call(storage, k);
					removed++;
				}
			}

			if (removed > 0) {
				this.opts.debug?.('LMS resume: cleared resume-like storage entries', { removed, storage: storage === localStorage ? 'local' : 'session' });
			}
		} catch (err) {
			this.opts.debug?.('LmsResumeStorageOverride.purgeResumeLikeEntries failed', err);
		}
	}

	disable(): void {
		if (this.lsGetItemOrig == null) return;
		try {
			localStorage.getItem = this.lsGetItemOrig;
			localStorage.setItem = this.lsSetItemOrig!;
			this.opts.debug?.('LMS resume: parent localStorage override removed');
		} catch (err) {
			this.opts.debug?.('LmsResumeStorageOverride.disable failed', err);
		}
		this.lsGetItemOrig = null;
		this.lsSetItemOrig = null;
		this.lsRemoveItemOrig = null;
	}
}

